import React, {useEffect, useState} from 'react';
import {useNavigate, useLocation} from "react-router-dom";
import api from "../../api";
import {Button, Col, Drawer, Form, Input, message, Row, Table, Tabs, Popconfirm, Card, Spin, Empty, Divider} from "antd";
import {EditOutlined, DeleteOutlined, SearchOutlined, PlusOutlined, FormOutlined} from '@ant-design/icons';

export default function AdminIcons() {
    const navigate = useNavigate();
    const location = useLocation();
    const [icons, setIcons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editDrawerOpen, setEditDrawerOpen] = useState(false);
    const [editingIcon, setEditingIcon] = useState(null);
    const [form] = Form.useForm();

    // New icon drawer state (combined search + manual)
    const [newIconDrawerOpen, setNewIconDrawerOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [addingIcon, setAddingIcon] = useState(null);
    const [showManualForm, setShowManualForm] = useState(false);
    const [manualForm] = Form.useForm();

    // Determine active tab based on route
    const getActiveTab = () => {
        if (location.pathname === '/admin/suppliers') return 'suppliers';
        if (location.pathname === '/admin/users') return 'users';
        if (location.pathname === '/admin/blog') return 'blog';
        if (location.pathname === '/admin/ad-spends') return 'ad-spends';
        if (location.pathname === '/admin/icons') return 'icons';
        if (location.pathname === '/admin/affiliates') return 'affiliates';
        return 'stats';
    };

    const handleTabChange = (key) => {
        switch(key) {
            case 'stats':
                navigate('/admin');
                break;
            case 'suppliers':
                navigate('/admin/suppliers');
                break;
            case 'users':
                navigate('/admin/users');
                break;
            case 'blog':
                navigate('/admin/blog');
                break;
            case 'ad-spends':
                navigate('/admin/ad-spends');
                break;
            case 'icons':
                navigate('/admin/icons');
                break;
            case 'affiliates':
                navigate('/admin/affiliates');
                break;
            default:
                navigate('/admin');
        }
    };

    useEffect(() => {
        loadIcons();
    }, []);

    const loadIcons = () => {
        setLoading(true);
        api.getIcons()
            .then(setIcons)
            .finally(() => setLoading(false));
    };

    // Open new icon drawer (combined search + manual)
    const handleNewIcon = () => {
        setSearchTerm('');
        setSearchResults([]);
        setShowManualForm(false);
        manualForm.resetFields();
        setNewIconDrawerOpen(true);
    };

    const handleCloseNewIconDrawer = () => {
        setNewIconDrawerOpen(false);
        setSearchTerm('');
        setSearchResults([]);
        setShowManualForm(false);
        manualForm.resetFields();
    };

    const handleEdit = (icon) => {
        setEditingIcon(icon);
        form.setFieldsValue({
            name: icon.name,
            url: icon.url,
        });
        setEditDrawerOpen(true);
    };

    const handleDelete = (id) => {
        api.deleteIcon(id)
            .then(() => {
                message.success('Icoon verwijderd');
                loadIcons();
            })
            .catch(() => message.error('Fout bij verwijderen'));
    };

    const handleEditSubmit = () => {
        form.validateFields().then((values) => {
            api.updateIcon(editingIcon.id, values)
                .then(() => {
                    message.success('Icoon bijgewerkt');
                    setEditDrawerOpen(false);
                    form.resetFields();
                    loadIcons();
                })
                .catch(() => message.error('Fout bij opslaan'));
        });
    };

    // Search Freepik
    const handleSearchFreepik = () => {
        if (!searchTerm.trim()) {
            message.warning('Voer een zoekterm in');
            return;
        }
        setSearching(true);
        setShowManualForm(false);
        api.searchFreepikIcons(searchTerm)
            .then((results) => {
                setSearchResults(results || []);
                if (!results || results.length === 0) {
                    message.info('Geen iconen gevonden');
                }
            })
            .catch(() => message.error('Fout bij zoeken'))
            .finally(() => setSearching(false));
    };

    // Add icon from Freepik search result
    const handleAddFromFreepik = (icon) => {
        setAddingIcon(icon.id);
        // Use the search query as the icon name instead of the Freepik icon name
        const iconName = searchTerm.trim();
        api.createIconFromFreepik(iconName, icon.thumbnail_url)
            .then(() => {
                message.success(`Icoon "${iconName}" toegevoegd`);
                loadIcons();
                // Remove from search results to prevent duplicates
                setSearchResults(prev => prev.filter(i => i.id !== icon.id));
            })
            .catch((error) => {
                const errorMsg = error.response?.data?.detail || 'Fout bij toevoegen';
                message.error(errorMsg);
            })
            .finally(() => setAddingIcon(null));
    };

    // Add icon manually
    const handleManualSubmit = () => {
        manualForm.validateFields().then((values) => {
            api.createIcon(values)
                .then(() => {
                    message.success('Icoon aangemaakt');
                    handleCloseNewIconDrawer();
                    loadIcons();
                })
                .catch(() => message.error('Fout bij opslaan'));
        });
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: 'Icoon',
            dataIndex: 'url',
            key: 'url',
            width: 100,
            render: (url) => url ? (
                <img src={url} alt="Icon" style={{maxWidth: 48, maxHeight: 48}} />
            ) : null,
        },
        {
            title: 'Naam',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'URL',
            dataIndex: 'url',
            key: 'url_text',
            ellipsis: true,
            render: (url) => url ? (
                <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
            ) : null,
        },
        {
            title: 'Acties',
            key: 'actions',
            width: 180,
            render: (_, icon) => (
                <Row gutter={8}>
                    <Col>
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(icon)}
                        >
                            Bewerk
                        </Button>
                    </Col>
                    <Col>
                        <Popconfirm
                            title="Weet je zeker dat je dit icoon wilt verwijderen?"
                            onConfirm={() => handleDelete(icon.id)}
                            okText="Ja"
                            cancelText="Nee"
                        >
                            <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                            >
                                Verwijder
                            </Button>
                        </Popconfirm>
                    </Col>
                </Row>
            ),
        },
    ];

    return (
        <>
            {/* Edit Drawer (for editing existing icons) */}
            <Drawer
                title="Icoon bewerken"
                width={500}
                open={editDrawerOpen}
                onClose={() => {
                    setEditDrawerOpen(false);
                    form.resetFields();
                }}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Naam"
                        rules={[{ required: true, message: 'Naam is verplicht' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="url"
                        label="URL"
                        rules={[{ required: true, message: 'URL is verplicht' }]}
                    >
                        <Input placeholder="https://..." />
                    </Form.Item>

                    {form.getFieldValue('url') && (
                        <Form.Item label="Preview">
                            <img
                                src={form.getFieldValue('url')}
                                alt="Preview"
                                style={{maxWidth: 128, maxHeight: 128}}
                            />
                        </Form.Item>
                    )}

                    <Form.Item>
                        <Row gutter={8}>
                            <Col>
                                <Button onClick={() => setEditDrawerOpen(false)}>
                                    Annuleren
                                </Button>
                            </Col>
                            <Col>
                                <Button type="primary" onClick={handleEditSubmit}>
                                    Bijwerken
                                </Button>
                            </Col>
                        </Row>
                    </Form.Item>
                </Form>
            </Drawer>

            {/* New Icon Drawer (Search + Manual entry combined) */}
            <Drawer
                title="Nieuw icoon toevoegen"
                width={900}
                open={newIconDrawerOpen}
                onClose={handleCloseNewIconDrawer}
            >
                {/* Search Section */}
                <div style={{marginBottom: 24}}>
                    <h3 style={{marginBottom: 12}}>
                        <SearchOutlined style={{marginRight: 8}} />
                        Zoek in Freepik
                    </h3>
                    <Row gutter={8}>
                        <Col flex="auto">
                            <Input
                                placeholder="Zoek iconen (bijv. 'tomato', 'carrot', 'lettuce')"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onPressEnter={handleSearchFreepik}
                                size="large"
                            />
                        </Col>
                        <Col>
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                onClick={handleSearchFreepik}
                                loading={searching}
                                size="large"
                            >
                                Zoeken
                            </Button>
                        </Col>
                    </Row>
                </div>

                {/* Search Results */}
                {searching ? (
                    <div style={{textAlign: 'center', padding: 40}}>
                        <Spin size="large" />
                    </div>
                ) : searchResults.length > 0 ? (
                    <div style={{marginBottom: 24}}>
                        <p style={{color: '#666', marginBottom: 12}}>
                            {searchResults.length} resultaten gevonden. Klik op "Toevoegen" om een icoon toe te voegen.
                        </p>
                        <Row gutter={[16, 16]}>
                            {searchResults.map((icon) => (
                                <Col key={icon.id} xs={12} sm={8} md={6}>
                                    <Card
                                        hoverable
                                        cover={
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                height: 100,
                                                padding: 10,
                                                backgroundColor: '#f5f5f5'
                                            }}>
                                                <img
                                                    alt={icon.name}
                                                    src={icon.thumbnail_url}
                                                    style={{maxWidth: 80, maxHeight: 80}}
                                                />
                                            </div>
                                        }
                                        actions={[
                                            <Button
                                                key="add"
                                                type="primary"
                                                size="small"
                                                icon={<PlusOutlined />}
                                                loading={addingIcon === icon.id}
                                                onClick={() => handleAddFromFreepik(icon)}
                                            >
                                                Toevoegen
                                            </Button>
                                        ]}
                                    >
                                        <Card.Meta
                                            title={icon.name}
                                            description={icon.style?.name || ''}
                                            style={{minHeight: 50}}
                                        />
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </div>
                ) : searchTerm && !searching ? (
                    <Empty
                        description="Geen resultaten gevonden"
                        style={{marginBottom: 24}}
                    />
                ) : null}

                {/* Divider and Manual Entry Section */}
                <Divider>of</Divider>

                {!showManualForm ? (
                    <div style={{textAlign: 'center'}}>
                        <Button
                            icon={<FormOutlined />}
                            onClick={() => setShowManualForm(true)}
                        >
                            Handmatig icoon toevoegen
                        </Button>
                    </div>
                ) : (
                    <div>
                        <h3 style={{marginBottom: 12}}>
                            <FormOutlined style={{marginRight: 8}} />
                            Handmatig toevoegen
                        </h3>
                        <Form form={manualForm} layout="vertical">
                            <Form.Item
                                name="name"
                                label="Naam"
                                rules={[{ required: true, message: 'Naam is verplicht' }]}
                            >
                                <Input placeholder="bijv. Tomaat" />
                            </Form.Item>

                            <Form.Item
                                name="url"
                                label="URL"
                                rules={[{ required: true, message: 'URL is verplicht' }]}
                            >
                                <Input placeholder="https://..." />
                            </Form.Item>

                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => prevValues.url !== currentValues.url}
                            >
                                {({ getFieldValue }) => {
                                    const url = getFieldValue('url');
                                    return url ? (
                                        <Form.Item label="Preview">
                                            <img
                                                src={url}
                                                alt="Preview"
                                                style={{maxWidth: 128, maxHeight: 128}}
                                                onError={(e) => {e.target.style.display = 'none'}}
                                            />
                                        </Form.Item>
                                    ) : null;
                                }}
                            </Form.Item>

                            <Form.Item>
                                <Row gutter={8}>
                                    <Col>
                                        <Button onClick={() => setShowManualForm(false)}>
                                            Annuleren
                                        </Button>
                                    </Col>
                                    <Col>
                                        <Button type="primary" onClick={handleManualSubmit}>
                                            Icoon aanmaken
                                        </Button>
                                    </Col>
                                </Row>
                            </Form.Item>
                        </Form>
                    </div>
                )}
            </Drawer>

            <h1>Admin</h1>

            <Tabs
                activeKey={getActiveTab()}
                onChange={handleTabChange}
                items={[
                    {
                        label: 'Stats',
                        key: 'stats',
                        children: null
                    },
                    {
                        label: 'Suppliers',
                        key: 'suppliers',
                        children: null
                    },
                    {
                        label: 'Icons',
                        key: 'icons',
                        children: (
                            <>
                                <Row style={{marginBottom: 16}}>
                                    <Col span={12}>
                                        <h2>Iconen</h2>
                                    </Col>
                                    <Col span={12} style={{textAlign: 'right'}}>
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={handleNewIcon}
                                        >
                                            Nieuw icoon
                                        </Button>
                                    </Col>
                                </Row>

                                <Table
                                    dataSource={icons}
                                    columns={columns}
                                    rowKey="id"
                                    loading={loading}
                                    pagination={{pageSize: 50}}
                                />
                            </>
                        )
                    },
                    {
                        label: 'Users',
                        key: 'users',
                        children: null
                    },
                    {
                        label: 'Blog Posts',
                        key: 'blog',
                        children: null
                    },
                    {
                        label: 'Ad Spends',
                        key: 'ad-spends',
                        children: null
                    },
                    {
                        label: 'Affiliates',
                        key: 'affiliates',
                        children: null
                    },
                ]}
            />
        </>
    );
}
